/**
 * The org's clock, for every time the screens show or read.
 *
 * A browser converts times with the tz data of its own build. When a country
 * changes its rules, an older browser reads another hour: Morocco went from
 * UTC+1 to UTC+0 on 20 September 2026 (tzdata 2026c), and a browser on 2026b
 * still shows an hour more. So the screens never use the browser's zone: the
 * API sends the org's offsets over five years, read from the database
 * (`utcOffsets` of the org profile), and every conversion goes through here.
 * Without them (no session yet, the platform console) the browser's zone is
 * used.
 */
import { shallowRef } from 'vue'

interface Change { from: number; offset: number }

/** Reactive: formatters built from the clock recompute when a session sets it. */
const changes = shallowRef<Change[] | null>(null)

/** Sets the org's offsets (from the org profile); null goes back to the browser's zone. */
export function setOrgOffsets(offsets: Array<{ from: string; offset: number }> | null | undefined): void {
  changes.value = offsets?.length
    ? offsets.map((o) => ({ from: Date.parse(o.from), offset: o.offset })).sort((a, b) => a.from - b.from)
    : null
}

/** The org's UTC offset at an instant, in minutes. */
export function offsetAt(t: number): number {
  const c = changes.value
  if (!c) return -new Date(t).getTimezoneOffset()
  let o = c[0]!.offset
  for (const x of c) { if (x.from <= t) o = x.offset; else break }
  return o
}

/** The same instant moved by the org's offset: its UTC fields are the org's wall clock. */
export const shifted = (d: Date): Date => new Date(d.getTime() + offsetAt(d.getTime()) * 60_000)

export interface WallClock {
  year: number; month: number; day: number; hours: number; minutes: number
  /** 0 Sunday … 6 Saturday, as Date.getDay(). */
  weekday: number
}

/** The org's wall clock at an instant. */
export function orgParts(d: Date): WallClock {
  const x = shifted(d)
  return { year: x.getUTCFullYear(), month: x.getUTCMonth() + 1, day: x.getUTCDate(),
           hours: x.getUTCHours(), minutes: x.getUTCMinutes(), weekday: x.getUTCDay() }
}

const pad = (n: number) => String(n).padStart(2, '0')

/** The org day of an instant, 'YYYY-MM-DD'. */
export function orgDay(d: Date = new Date()): string {
  const p = orgParts(d)
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`
}

/** The instant of a wall-clock time in the org's zone (month 1–12; day may overflow, as Date.UTC). */
export function fromOrg(year: number, month: number, day: number, hours = 0, minutes = 0): Date {
  const wall = Date.UTC(year, month - 1, day, hours, minutes)
  // The offset of the result, not of the wall time read as UTC: twice settles a change of offset.
  let t = wall - offsetAt(wall) * 60_000
  t = wall - offsetAt(t) * 60_000
  return new Date(t)
}

/** Value of a `datetime-local` field: the org's wall clock, 'YYYY-MM-DDTHH:MM'. */
export function toLocalField(d: Date): string {
  const p = orgParts(d)
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hours)}:${pad(p.minutes)}`
}

/** The instant a `datetime-local` value names in the org's zone, ISO; null when unreadable. */
export function fromLocalField(v: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(v)
  return m ? fromOrg(+m[1]!, +m[2]!, +m[3]!, +m[4]!, +m[5]!).toISOString() : null
}

/** The UTC offset of `tz` at `at` according to this browser, in minutes; null for an unknown zone. */
export function browserOffset(tz: string, at: Date = new Date()): number | null {
  try {
    const p = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    }).formatToParts(at).map((x) => [x.type, x.value]))
    const local = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), Number(p.hour), Number(p.minute))
    return Math.round((local - Math.floor(at.getTime() / 60_000) * 60_000) / 60_000)
  } catch {
    return null
  }
}

/** "UTC+1", "UTC−3", "UTC+5:30" for an offset in minutes. */
export function offsetLabel(n: number): string {
  const a = Math.abs(n)
  return `UTC${n < 0 ? '−' : '+'}${Math.floor(a / 60)}${a % 60 ? `:${pad(a % 60)}` : ''}`
}

/**
 * "Africa/Casablanca (UTC+0)": a zone and its offset now. The org's own zone
 * takes the server's offset; another one, this browser's. Never a fixed
 * label: offsets change (Morocco, 20 September 2026) and summer time moves them.
 */
export function zoneLabel(tz: string, org?: { timezone?: string; utcOffsetMinutes?: number } | null, at: Date = new Date()): string {
  if (tz === 'UTC') return 'UTC'
  const n = tz === org?.timezone && org.utcOffsetMinutes !== undefined ? org.utcOffsetMinutes : browserOffset(tz, at)
  return n === null ? tz : `${tz} (${offsetLabel(n)})`
}
